import * as Sentry from '@sentry/nextjs'
import {
  handleFetchResponses,
  handleFetchs,
  type FetchCardsSlugComplex,
} from './use-fetch-cards-helper'
import type { CompareCredit } from '../../types'
import type { SORT_DATA } from './sort/types/experimentation-types'
import { SORT_MAP } from './sort/sort-map'
import assocPath from 'lodash/fp/assocPath'
import { checkIfEmptyCardsList } from '.'
import { formatLocation } from './formatLocation'
import { getExpSlugsToHide } from './experimentation/get-exp-slugs-to-hide'
import getUserAgentInfo from './getUserAgentInfo'
import { handleSort } from './sort/server/sort-handler'
import includes from 'lodash/fp/includes'
import map from 'lodash/fp/map'
import type { SortResponseInterface } from './sort/types'

export const getSortConfig = (statsigSort: string) => {
  const sortConfig = SORT_MAP[statsigSort]
  if (!sortConfig) {
    Sentry.captureException(new Error(`Sort not found: ${statsigSort}`))
  }
  return sortConfig || SORT_MAP['default']
}

export const applySortToEntities = (
  entities: CompareCredit.Entity[],
  sortConfig: SORT_DATA | null,
) => {
  const [sort, version] = sortConfig?.sort || []
  const addSort = map(assocPath(['context', 'sort'], `${sort}:${version}`))
  const hasSort = !!sort && !!version
  const entitiesToRender = (hasSort ? addSort(entities) : entities) as any
  return entitiesToRender
}

export const fetchCardsSlugComplex = async ({
  slugs,
  sortData,
  sort,
  adjustment,
  attribution,
  clientGeoLocation,
  getDynamicConfig,
  getExperiment,
  callback,
}: FetchCardsSlugComplex) => {
  const atnPrefixRegex = /^atn_.*/
  const utmSource = atnPrefixRegex.test(attribution?.utm_source ?? '')
    ? 'atn'
    : attribution?.utm_source

  const utmParams = {
    ad_group_id: attribution?.ad_group_id ?? '',
    campaign_medium: attribution?.utm_medium ?? '',
    campaign_source: utmSource ?? '',
    campaign_name: attribution?.utm_campaign ?? '',
  }

  const sortConfig = getSortConfig(sort)

  const { browser, os, device } = getUserAgentInfo()

  const sortDataWithUtmParams = {
    ...sortData,
    ...utmParams,
    position: '1',
    user_agent_os_name: os?.name,
    user_agent_browser_name: browser?.name,
    user_agent_device_type: device?.type,
    location_city: formatLocation(clientGeoLocation.city),
    location_country: formatLocation(clientGeoLocation.country),
    location_state: formatLocation(clientGeoLocation.state),
    page_path: window.location.pathname,
    proximity_to_target_adjustment: adjustment,
    entry_query_ad_group_id: attribution?.ad_group_id ?? '',
    entry_query_kw: attribution?.kw ?? '',
    entry_query_network: attribution?.network ?? '',
    entry_query_placement: attribution?.placement ?? '',
    entry_query_utm_campaign: attribution?.utm_campaign ?? '',
    entry_query_utm_content: attribution?.utm_content ?? '',
    entry_query_utm_medium: attribution?.utm_medium ?? '',
    entry_query_utm_source: utmSource,
  }

  const config = getDynamicConfig('hide_cards_config')
  const capitalOneHideCardConfig = getDynamicConfig(
    'capitalone_hide_card_config',
  )

  const slugsToHide = [
    ...config.get('cards_to_hide', []),
    ...capitalOneHideCardConfig.get('cards_to_hide', []),
  ]

  const geoConfig = getDynamicConfig('geo_location_config')
  const geoSlugsToHide = geoConfig.get('cards_to_hide', [])

  // CC-2420 - Pinning Reflect to Second Spot for /credit-cards/best/low-interest
  const reflectConfig = getDynamicConfig('reflect_low_balance')
  const reflectConfigArray = reflectConfig.get('second_position_slug', [])

  // 2023.9 Dynamic Config: hide_card_on_path_experiment_names
  //get Dynamic Config that holds array of experiment names
  const experimentNames = getDynamicConfig(
    'hide_card_on_path_experiment_names',
  ).get('experiment_names', []) as string[]

  const expSlugsToHide = getExpSlugsToHide(experimentNames, getExperiment)

  const slugsToRender = slugs.filter(
    (slug) =>
      !includes(slug, [...slugsToHide, ...geoSlugsToHide, ...expSlugsToHide]),
  )

  const [entities, sortedEntities]: [
    entities: PromiseSettledResult<any>,
    sortedEntities: PromiseSettledResult<SortResponseInterface>,
  ] = await handleFetchs(slugsToRender, sortDataWithUtmParams, sortConfig)

  if (
    sortedEntities.status === 'fulfilled' &&
    entities.status === 'fulfilled'
  ) {
    const allEntitiesSorted = handleSort(
      entities.value,
      sortedEntities.value,
    ) as unknown as CompareCredit.Entity[]
    checkIfEmptyCardsList(entities.value.cards)
    if (allEntitiesSorted && allEntitiesSorted.length > 0) {
      // CC-2420
      // If reflectConfigArray is provided, reposition it to the second position.
      if (reflectConfigArray && reflectConfigArray.length > 0) {
        const secondPostionSlug = reflectConfigArray[0]
        const index = allEntitiesSorted.findIndex(
          (entity) => entity.slug === secondPostionSlug,
        )
        if (index > -1) {
          const [movedEntity] = allEntitiesSorted.splice(index, 1)
          allEntitiesSorted.splice(1, 0, movedEntity)
        }

        // ✅ Reassign correct sortPosition to each entity
        allEntitiesSorted.forEach((entity, idx) => {
          ;(entity as unknown as { sortPosition: number }).sortPosition = idx
        })

        return callback({
          cards: applySortToEntities(allEntitiesSorted, sortConfig),
          isFetching: false,
          error: false,
          sort,
        })
      }
      return callback({
        cards: applySortToEntities(allEntitiesSorted, sortConfig),
        isFetching: false,
        error: false,
        sort,
      })
    } else {
      Sentry.captureException(new Error('entities sort failed'))
      return callback({ cards: [], isFetching: false, error: true, sort })
    }
  } else {
    const { fetchError, fetchedEntities } = handleFetchResponses(
      entities,
      sortedEntities,
    )
    return callback({
      cards: fetchedEntities,
      isFetching: false,
      error: fetchError,
      sort,
    })
  }
}
